import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CustomerRating {
  id: string;
  restaurant_id: string;
  table_number: string | null;
  order_id: string | null;
  food_quality: number | null;
  service_quality: number | null;
  environment: number | null;
  value_for_money: number | null;
  overall_experience: number | null;
  comments: string | null;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  accept_marketing: boolean | null;
  created_at: string;
}

export interface RatingStats {
  totalRatings: number;
  avgFoodQuality: number;
  avgServiceQuality: number;
  avgEnvironment: number;
  avgValueForMoney: number;
  avgOverallExperience: number;
  nps: number;
  promoters: number;
  detractors: number;
  passives: number;
}

export function useRatingsReport(restaurantId: string | undefined, dateRange?: { start: Date; end: Date }) {
  const { data: ratings = [], isLoading, error } = useQuery({
    queryKey: ["ratings-report", restaurantId, dateRange?.start, dateRange?.end],
    queryFn: async () => {
      if (!restaurantId) return [];

      let query = supabase
        .from("customer_ratings")
        .select("*")
        .eq("restaurant_id", restaurantId)
        .order("created_at", { ascending: false });

      if (dateRange?.start) {
        query = query.gte("created_at", dateRange.start.toISOString());
      }
      if (dateRange?.end) {
        query = query.lte("created_at", dateRange.end.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as CustomerRating[];
    },
    enabled: !!restaurantId,
  });

  // Calculate statistics
  const stats: RatingStats = {
    totalRatings: ratings.length,
    avgFoodQuality: 0,
    avgServiceQuality: 0,
    avgEnvironment: 0,
    avgValueForMoney: 0,
    avgOverallExperience: 0,
    nps: 0,
    promoters: 0,
    detractors: 0,
    passives: 0,
  };

  if (ratings.length > 0) {
    let foodSum = 0, foodCount = 0;
    let serviceSum = 0, serviceCount = 0;
    let envSum = 0, envCount = 0;
    let valueSum = 0, valueCount = 0;
    let overallSum = 0, overallCount = 0;

    for (const rating of ratings) {
      if (rating.food_quality) { foodSum += rating.food_quality; foodCount++; }
      if (rating.service_quality) { serviceSum += rating.service_quality; serviceCount++; }
      if (rating.environment) { envSum += rating.environment; envCount++; }
      if (rating.value_for_money) { valueSum += rating.value_for_money; valueCount++; }
      if (rating.overall_experience) {
        overallSum += rating.overall_experience;
        overallCount++;
        
        // NPS calculation (5 = promoter, 4 = passive, 1-3 = detractor)
        if (rating.overall_experience === 5) stats.promoters++;
        else if (rating.overall_experience === 4) stats.passives++;
        else stats.detractors++;
      }
    }

    stats.avgFoodQuality = foodCount > 0 ? foodSum / foodCount : 0;
    stats.avgServiceQuality = serviceCount > 0 ? serviceSum / serviceCount : 0;
    stats.avgEnvironment = envCount > 0 ? envSum / envCount : 0;
    stats.avgValueForMoney = valueCount > 0 ? valueSum / valueCount : 0;
    stats.avgOverallExperience = overallCount > 0 ? overallSum / overallCount : 0;

    // NPS = % promoters - % detractors
    const totalNps = stats.promoters + stats.passives + stats.detractors;
    if (totalNps > 0) {
      stats.nps = Math.round(((stats.promoters - stats.detractors) / totalNps) * 100);
    }
  }

  return {
    ratings,
    stats,
    isLoading,
    error,
  };
}
